/**
 * walleu-samples
 * Free-samples selector. Each section instance manages its own state.
 * - No Swiper: the carousel is native CSS scroll-snap.
 * - Money is formatted with Intl using the active presentment currency (multi-market safe).
 * - Add-to-cart mirrors the v7 ProductForm flow so the cart drawer opens and refreshes.
 */
(function () {
  const money = new Intl.NumberFormat(Shopify.locale, {
    style: 'currency',
    currency: Shopify.currency.active,
  });

  class WalleuSamples {
    constructor(root) {
      this.root = root;
      this.chosen = new Map(); // variantId -> { price, imgSrc }
      this.cards = Array.from(root.querySelectorAll('[data-sample]'));
      this.chosenEl = root.querySelector('[data-chosen]');
      this.emptyEl = root.querySelector('[data-empty]');
      this.countEl = root.querySelector('[data-count]');
      this.totalEl = root.querySelector('[data-total]');
      this.addBtn = root.querySelector('[data-add]');

      this.cards.forEach((card) => card.addEventListener('click', () => this.toggle(card)));
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.addToCart());
      this.render();
    }

    toggle(card) {
      const id = card.dataset.variantId;
      if (this.chosen.has(id)) {
        this.chosen.delete(id);
        card.setAttribute('aria-pressed', 'false');
      } else {
        const img = card.querySelector('img');
        this.chosen.set(id, {
          price: parseInt(card.dataset.price, 10) || 0,
          // Match the old theme: the tray thumbnail uses the variant featured image,
          // falling back to the swatch shown in the carousel.
          imgSrc: card.dataset.chosenSrc || (img ? img.currentSrc || img.src : ''),
        });
        card.setAttribute('aria-pressed', 'true');
      }
      this.render();
    }

    render() {
      const count = this.chosen.size;

      // count + total
      if (this.countEl) this.countEl.textContent = count;
      let cents = 0;
      this.chosen.forEach((s) => (cents += s.price));
      if (this.totalEl) this.totalEl.textContent = money.format(cents / 100);

      // add button state
      if (this.addBtn) this.addBtn.toggleAttribute('disabled', count === 0);

      // empty state
      if (this.emptyEl) this.emptyEl.style.display = count === 0 ? '' : 'none';

      // chosen thumbnails
      if (this.chosenEl) {
        this.chosenEl.querySelectorAll('.walleu-samples__chosen-thumb').forEach((t) => t.remove());
        this.chosen.forEach((s) => {
          if (!s.imgSrc) return;
          const img = document.createElement('img');
          img.src = s.imgSrc;
          img.alt = '';
          img.className = 'walleu-samples__chosen-thumb';
          this.chosenEl.appendChild(img);
        });
      }
    }

    async addToCart() {
      if (this.chosen.size === 0) return;
      const items = Array.from(this.chosen.keys()).map((id) => ({ id: parseInt(id, 10), quantity: 1 }));

      // Ask the cart drawer (and any other listeners) which sections to bundle in the response.
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );

      this.addBtn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
        });
        const added = await res.json();

        if (!res.ok) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = added.sections;

        // Open the drawer and refresh its contents, exactly like the native product form.
        this.root.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: added.items || [added], cart },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
        );

        this.reset();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.addBtn.removeAttribute('aria-busy');
      }
    }

    reset() {
      this.chosen.clear();
      this.cards.forEach((c) => c.setAttribute('aria-pressed', 'false'));
      this.render();
    }
  }

  function init() {
    document.querySelectorAll('[data-walleu-samples]').forEach((root) => {
      if (root.dataset.inited) return;
      root.dataset.inited = 'true';
      new WalleuSamples(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
